// ===========================================================================
// The sign-in proof of work, solved.
//
// Past a username's sign-in budget the server answers a device it does not
// know with a challenge (server/src/login-work.js), and admits the attempt
// that comes back with a counter such that SHA-256 over the challenge followed
// by the counter in decimal begins with `difficulty` zero bits.
//
// Plain JavaScript SHA-256 rather than crypto.subtle: subtle answers with a
// promise, and a million awaited digests take many times longer than a million
// computed in a loop. The challenge's whole blocks are hashed once, and only
// the tail with the counter is hashed for each counter. No DOM and no config
// import, so this runs in a Web Worker and under the node test runner. ASCII
// only.
// ===========================================================================

function primes(count) {
  const out = [];
  for (let n = 2; out.length < count; n += 1) {
    if (out.every((p) => n % p !== 0)) out.push(n);
  }
  return out;
}
const fraction32 = (x) => ((x - Math.floor(x)) * 0x100000000) | 0;
const FIRST_PRIMES = primes(64);
/** The round constants: the first 32 bits of the fractional parts of the cube roots of the first 64 primes. */
const K = Int32Array.from(FIRST_PRIMES, (p) => fraction32(Math.cbrt(p)));
/** The initial state: the same, from the square roots of the first 8 primes. */
const IV = Int32Array.from(FIRST_PRIMES.slice(0, 8), (p) => fraction32(Math.sqrt(p)));

/** One SHA-256 compression of the 64 bytes of `m` at `o` into the state `h`, with `w` as scratch. */
function compress(h, m, o, w) {
  for (let i = 0; i < 16; i += 1) {
    const j = o + i * 4;
    w[i] = (m[j] << 24) | (m[j + 1] << 16) | (m[j + 2] << 8) | m[j + 3];
  }
  for (let i = 16; i < 64; i += 1) {
    const x = w[i - 15];
    const y = w[i - 2];
    const s0 = ((x >>> 7) | (x << 25)) ^ ((x >>> 18) | (x << 14)) ^ (x >>> 3);
    const s1 = ((y >>> 17) | (y << 15)) ^ ((y >>> 19) | (y << 13)) ^ (y >>> 10);
    w[i] = (s1 + w[i - 7] + s0 + w[i - 16]) | 0;
  }
  let a = h[0];
  let b = h[1];
  let c = h[2];
  let d = h[3];
  let e = h[4];
  let f = h[5];
  let g = h[6];
  let k = h[7];
  for (let i = 0; i < 64; i += 1) {
    const t1 = (k + (((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7))) + ((e & f) ^ (~e & g)) + K[i] + w[i]) | 0;
    const t2 = ((((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10))) + ((a & b) ^ (a & c) ^ (b & c))) | 0;
    k = g;
    g = f;
    f = e;
    e = (d + t1) | 0;
    d = c;
    c = b;
    b = a;
    a = (t1 + t2) | 0;
  }
  h[0] = (h[0] + a) | 0;
  h[1] = (h[1] + b) | 0;
  h[2] = (h[2] + c) | 0;
  h[3] = (h[3] + d) | 0;
  h[4] = (h[4] + e) | 0;
  h[5] = (h[5] + f) | 0;
  h[6] = (h[6] + g) | 0;
  h[7] = (h[7] + k) | 0;
}

/** Write the SHA-256 padding for a message of `length` bytes into `buf` from `at`; returns the bytes in whole blocks. */
function pad(buf, at, length) {
  let p = at;
  buf[p++] = 0x80;
  const total = Math.ceil((p + 8) / 64) * 64;
  while (p < total - 8) buf[p++] = 0;
  const bits = length * 8;
  const high = Math.floor(bits / 0x100000000);
  buf[total - 8] = high >>> 24;
  buf[total - 7] = high >>> 16;
  buf[total - 6] = high >>> 8;
  buf[total - 5] = high;
  buf[total - 4] = bits >>> 24;
  buf[total - 3] = bits >>> 16;
  buf[total - 2] = bits >>> 8;
  buf[total - 1] = bits;
  return total;
}

/** SHA-256 of a byte array, as 32 bytes. */
export function sha256(bytes) {
  const whole = bytes.length - (bytes.length % 64);
  const h = Int32Array.from(IV);
  const w = new Int32Array(64);
  for (let o = 0; o < whole; o += 64) compress(h, bytes, o, w);
  const tail = new Uint8Array(128);
  tail.set(bytes.subarray(whole));
  const total = pad(tail, bytes.length - whole, bytes.length);
  for (let o = 0; o < total; o += 64) compress(h, tail, o, w);
  const out = new Uint8Array(32);
  for (let i = 0; i < 8; i += 1) {
    out[i * 4] = h[i] >>> 24;
    out[i * 4 + 1] = h[i] >>> 16;
    out[i * 4 + 2] = h[i] >>> 8;
    out[i * 4 + 3] = h[i];
  }
  return out;
}

/** Whether a digest begins with at least `difficulty` zero bits. */
export function meetsDifficulty(digest, difficulty) {
  if (!Number.isInteger(difficulty) || difficulty < 1 || !digest || difficulty > digest.length * 8) return false;
  const whole = difficulty >> 3;
  const rest = difficulty & 7;
  for (let i = 0; i < whole; i += 1) if (digest[i] !== 0) return false;
  return rest === 0 || digest[whole] >> (8 - rest) === 0;
}

/** A difficulty this solver takes: whole bits from 1 to 32, as the server issues them. */
export function validDifficulty(difficulty) {
  return Number.isInteger(difficulty) && difficulty >= 1 && difficulty <= 32;
}

/**
 * A search through the counters from 0, a stretch at a time: next(count) tries
 * the next `count` counters and returns the first that does the work, or -1.
 */
export function createSearch(challenge, difficulty) {
  if (typeof challenge !== 'string' || !challenge || !validDifficulty(difficulty)) throw new Error('bad_work');
  const prefix = new TextEncoder().encode(challenge);
  const whole = prefix.length - (prefix.length % 64);
  const mid = Int32Array.from(IV);
  const w = new Int32Array(64);
  for (let o = 0; o < whole; o += 64) compress(mid, prefix, o, w);
  const tailLength = prefix.length - whole;
  // The tail (under 64 bytes), a counter of up to 16 digits, and the padding: two blocks at most.
  const buf = new Uint8Array(128);
  buf.set(prefix.subarray(whole));
  const h = new Int32Array(8);
  const digits = new Uint8Array(16);
  const shift = 32 - difficulty;
  let counter = 0;
  return {
    next(count) {
      for (const end = counter + count; counter < end; counter += 1) {
        let n = counter;
        let len = 0;
        do {
          digits[len++] = 48 + (n % 10);
          n = Math.floor(n / 10);
        } while (n > 0);
        let p = tailLength;
        for (let i = len - 1; i >= 0; i -= 1) buf[p++] = digits[i];
        const total = pad(buf, p, whole + p);
        h.set(mid);
        compress(h, buf, 0, w);
        if (total > 64) compress(h, buf, 64, w);
        // The first 32 bits of the digest are the first word of the state.
        if (h[0] >>> shift === 0) {
          const found = counter;
          counter += 1;
          return found;
        }
      }
      return -1;
    },
  };
}

/** The first counter that does the work, found in one go. */
export function solveLoginWorkSync(challenge, difficulty) {
  const search = createSearch(challenge, difficulty);
  for (;;) {
    const counter = search.next(1 << 20);
    if (counter >= 0) return counter;
  }
}

export default { sha256, meetsDifficulty, validDifficulty, createSearch, solveLoginWorkSync };
